import React, { useContext, useMemo, useState } from 'react'
import { orderBy } from 'lodash'
import {
  ColorMarker,
  Charts,
  PrettyNumber,
  IGridTableColumn,
  NewGridTable,
  GridTableStatusEnum,
} from '@ui'
import { PrintTitle, TabContext } from '../Details'
import { IPortfolioSpace, Order } from '@api'
import classnames from 'classnames'
import { Classes } from '@blueprintjs/core'

type Sorts = 'workstations' | 'readableType' | 'areaSqft'

export default function SpaceTypes() {
  const { object, units, loading } = useContext(TabContext)
  let spaceTypes = object?.spaceTypes

  const scale = useMemo(() => Charts.colorScale(), [])
  const [sort, setSort] = useState<Sorts>('workstations')
  const [sortAscending, setSortAscending] = useState(false)

  const countData =
    spaceTypes?.map((s) => {
      return {
        label: s.readableType,
        value: s.workstations,
        color: `#${scale(s.spaceType)}`,
      }
    }) || []
  const allocatedAreaData =
    spaceTypes?.map((s) => {
      return {
        label: s.readableType,
        value: units === 'sqft' ? s.areaSqft : s.areaSqrm,
        color: `#${scale(s.spaceType)}`,
      }
    }) || []

  const columns: IGridTableColumn<IPortfolioSpace & { id: string }>[] = [
    {
      label: 'Space Type',
      sort: 'readableType',
      Cell: ({ cell, skeleton }) => {
        return (
          <div data-test="space-type" className={classnames({ [Classes.SKELETON]: skeleton })}>
            {cell ? (
              <div className="f f-align-center nowrap">
                {cell.data.id !== 'total' && (
                  <ColorMarker color={`#${scale(cell.data.spaceType)}`} />
                )}
                {cell.data.readableType}
              </div>
            ) : (
              'placeholder'
            )}
          </div>
        )
      },
    },
    {
      sort: 'workstations',
      label: 'Spaces',
      Cell: ({ cell, skeleton }) => {
        return (
          <div data-test="spaces" className={classnames({ [Classes.SKELETON]: skeleton })}>
            {cell ? <PrettyNumber value={cell.data.workstations} numeralFormat="0,0" /> : 'place'}
          </div>
        )
      },
    },
    {
      sort: 'areaSqft',
      label: `Allocated Area (${units === 'sqft' ? 'Sqft' : 'Sqrm'})`,
      Cell: ({ cell, skeleton }) => {
        return (
          <div data-test="area-units" className={classnames({ [Classes.SKELETON]: skeleton })}>
            {cell ? (
              <PrettyNumber
                value={units === 'sqft' ? cell.data.areaSqft : cell.data.areaSqrm}
                numeralFormat="0,0a"
              />
            ) : (
              'place'
            )}
          </div>
        )
      },
    },
  ]

  spaceTypes = orderBy(spaceTypes, sort, sortAscending ? 'asc' : 'desc')

  const formatSpaceTypes = (items: IPortfolioSpace[]) => {
    return items.map((s) => ({
      data: { id: s.spaceType, ...s },
    }))
  }

  let formatedSpaceTypes = formatSpaceTypes(
    orderBy(spaceTypes, sort, sortAscending ? 'asc' : 'desc')
  )

  //todo use table total line feature (appendData, appendColumn)
  formatedSpaceTypes = [
    ...formatedSpaceTypes,
    ...(object
      ? [
          {
            data: {
              id: 'total',
              areaSqft: spaceTypes.reduce((acc, s) => acc + s.areaSqft, 0),
              areaSqrm: spaceTypes.reduce((acc, s) => acc + s.areaSqrm, 0),
              occupied: 0,
              readableType: 'Total',
              spaceType: '',
              workstations: spaceTypes.reduce((acc, s) => acc + s.workstations, 0),
            },
          },
        ]
      : []),
  ]

  return (
    <>
      <PrintTitle>
        <h3>Space Types</h3>
      </PrintTitle>
      <table className="basic-table">
        <tbody>
          <tr>
            <td />
            <td>
              <Charts.Donut title="Count" data={countData} height={270} width={370} />
            </td>
            <td>
              <Charts.Donut
                title="Allocated Area"
                data={allocatedAreaData}
                height={270}
                width={370}
              />
            </td>
          </tr>
        </tbody>
      </table>
      <NewGridTable<IPortfolioSpace & { id: string }>
        status={loading ? GridTableStatusEnum.LOADING : GridTableStatusEnum.IDLE}
        columns={columns}
        data={formatedSpaceTypes}
        sort={{
          column: sort,
          order: sortAscending ? Order.ASC : Order.DESC,
          onSort: (column: string) => {
            if (sort === column) {
              return setSortAscending(!sortAscending)
            } else {
              setSort(column as Sorts)
              setSortAscending(false)
            }
          },
        }}
      />
    </>
  )
}
